import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { account, cancelled, deviceName, mailSent, NOT_CONFIRMED, sentWhen, passkeysSupported, serverFeatures, SPAM_FOLDER, wordFor } from "../account.ts";
import type { Passkey, Session, SignedIn, User } from "../account.ts";
import { useCatalog, useStore } from "../hooks.ts";
import { keepsVintages } from "../catalog.ts";
import { LegalLinks, messageOf } from "./Login.tsx";
import { Team } from "./Team.tsx";
import { ROLE_LABELS, SETTINGS_ID, drinkName, editsMasterData, isProfiled, pantryPricesOn, profileOf } from "@lagerlog/core";
import { SpeiseplanLink } from "./SpeiseplanLink.tsx";
import { EinkaufLink } from "./EinkaufLink.tsx";
import { Data } from "./Data.tsx";
import { IS_PANTRY, WORDS } from "../brand.ts";

const date = (at: number) => new Date(at).toLocaleDateString("de-DE", { day: "numeric", month: "short", year: "numeric" });

/** "Mein Konto": name, e-mail address, passkeys, signed-in devices, password. Needs the network. */
export function Account() {
  const { store } = useStore();
  const [me, setMe] = useState<(SignedIn & { session: string; demo?: boolean; emailSentAt?: number }) | null>(null);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [passkeysOn, setPasskeysOn] = useState(false);
  const [mailOn, setMailOn] = useState(false);

  async function load() {
    setError(null);
    try {
      const [m, p, s, f] = await Promise.all([account.me(), account.passkeys(), account.sessions(), serverFeatures()]);
      setPasskeysOn(f.passkeys);
      setMailOn(f.mail);
      setMe(m);
      setPasskeys(p);
      setSessions(s);
    } catch (err) {
      setError(messageOf(err));
    }
  }
  useEffect(() => {
    if (!store.config?.token) void load();
  }, [store.config?.token]);

  const pending = store.replica?.pending.length ?? 0;
  async function signOut() {
    const warning = pending > 0 ? `\n\nAchtung: ${pending} Änderungen sind noch nicht abgeglichen und gehen verloren.` : "";
    if (!confirm(`Auf diesem Gerät abmelden? Die Daten auf dem Gerät werden gelöscht; im ${wordFor(store.config?.kind).one} bleibt alles.${warning}`)) return;
    await store.signOutAccount();
    location.hash = "#/anmelden";
  }

  if (store.config?.token) {
    return (
      <div className="account">
        <p>Dieses Gerät nutzt einen Zugang aus der Testphase, kein Konto.</p>
        <div className="buttons">
          <a className="button" href="#/lager">
            Lager bearbeiten
          </a>
          <a className="button" href="#/status">
            Status
          </a>
          <button className="danger" onClick={signOut}>
            Abmelden
          </button>
        </div>
        <LegalLinks />
      </div>
    );
  }

  if (!me) {
    return (
      <div className="account">
        {error ? <p className="error-box">{error}</p> : <p className="muted">Lädt …</p>}
        <div className="buttons">
          {error && <button onClick={load}>Noch einmal</button>}
          <a className="button" href="#/status">
            Status
          </a>
          <button className="danger" onClick={signOut}>
            Abmelden
          </button>
        </div>
        <LegalLinks />
      </div>
    );
  }

  const workspace = me.workspaces.find((w) => w.id === store.config?.workspaceId);
  const word = wordFor(workspace?.kind);
  return (
    <div className="account">
      <section className="card">
        <p>
          Angemeldet als <strong>{me.user.displayName}</strong>
          {workspace && (workspace.role === "owner" ? ` · Inhaber ${word.of} ${workspace.name}` : ` · ${word.one} ${workspace.name}, Rolle „${ROLE_LABELS[workspace.role]}“`)}
        </p>
        <p className="muted">Benutzername: {me.user.username}</p>
        {me.demo ? (
          <p className="hint" data-testid="demo-note">
            Demo-Zugang: Hier darf alles ausprobiert werden. Jede Nacht um 4 Uhr wird der {word.one} zurückgesetzt. Name, Passwort und Team bleiben fest.
          </p>
        ) : (
            <>
            <Field label="Dein Name" value={me.user.displayName} hint="So nennen dich die anderen in der App." save={async (v) => setMe({ ...me, user: (await account.setName(v)).user })} done="Name gespeichert" testid="my-name" />
            <Field
              label="E-Mail-Adresse"
              type="email"
              value={me.user.email ?? ""}
              hint="Damit du dein Passwort selbst zurücksetzen kannst. Nie für Werbung."
              save={async (v) => {
                const { user, mailed } = await account.setEmail(v);
                setMe({ ...me, user });
                if (mailed === "sent") return mailSent(user.emailPending ?? user.email ?? v);
                if (mailed === "limited") return `E-Mail-Adresse gespeichert. Gerade sind schon einige Mails an dich unterwegs; den Link schickst du dir später unter „Mail noch einmal schicken“. ${SPAM_FOLDER}`;
              }}
              done="E-Mail-Adresse gespeichert"
              testid="my-email"
            />
            {mailOn && <EmailState user={me.user} sentAt={me.emailSentAt ?? null} />}
            </>
        )}
      </section>

      {passkeysOn && !me.demo && (
        <section className="card">
          <h2>Passkeys</h2>
          <p className="muted">Anmelden mit Face ID, Fingerabdruck oder Geräte-PIN statt Passwort. Das Passwort bleibt bestehen.</p>
          <Passkeys passkeys={passkeys} onChange={setPasskeys} />
        </section>
      )}

      {!me.demo && <Team />}

      <Data />

      {IS_PANTRY && editsMasterData(store.rights().role) && <EinkaufLink />}
      {IS_PANTRY && editsMasterData(store.rights().role) && <SpeiseplanLink />}

      {!IS_PANTRY && editsMasterData(store.rights().role) && <VintagesSetting />}
      {IS_PANTRY && editsMasterData(store.rights().role) && <PricesSetting />}

      <section className="card">
        <h2>Lager und Lagerorte</h2>
        <p className="muted">{WORDS.lagers}: umbenennen, ergänzen, und welche Lager dieses Gerät führt.</p>
        <a className="button" href="#/lager" data-testid="places">
          Lager bearbeiten
        </a>
      </section>

      <section className="card">
        <h2>Angemeldete Geräte</h2>
        <ul className="list">
          {sessions.map((s) => (
            <li key={s.id} className="entry">
              <div className="entry-text">
                {s.device || "Gerät"} {s.current && <strong>(dieses Gerät)</strong>}
                <br />
                <span className="muted">
                  seit {date(s.createdAt)} · zuletzt {date(s.lastSeenAt)}
                </span>
              </div>
              {!s.current && (
                <button
                  className="link danger entry-action"
                  onClick={async () => {
                    if (!confirm(`„${s.device}“ abmelden? Wer das Gerät hat, kommt dann nicht mehr an die Daten auf dem Server.`)) return;
                    await account.endSession(s.id).catch((err) => setError(messageOf(err)));
                    setSessions(await account.sessions());
                  }}
                >
                  Abmelden
                </button>
              )}
            </li>
          ))}
        </ul>
        <p className="hint">Ein verlorenes Handy hier abmelden. Was es offline schon gespeichert hat, bleibt auf ihm, gleicht aber nicht mehr ab.</p>
      </section>

      {!me.demo && (
        <section className="card">
          <details>
            <summary>Passwort ändern</summary>
            <PasswordForm />
          </details>
        </section>
      )}

      {IS_PANTRY && <DataSources />}

      {error && <p className="error-box">{error}</p>}
      <div className="buttons">
        <a className="button" href="#/status">
          Status
        </a>
        <button className="danger" onClick={signOut} data-testid="logout">
          Abmelden
        </button>
      </div>
      <LegalLinks />
    </div>
  );
}

/** `save` may answer with its own message instead of `done`. */
function Field(props: { label: string; value: string; hint: string; type?: string; save: (v: string) => Promise<string | void>; done: string; testid: string }) {
  const [value, setValue] = useState(props.value);
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    setState({ busy: true });
    try {
      setState({ ok: (await props.save(value.trim())) || props.done });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        {props.label}
        <span className="row">
          <input type={props.type ?? "text"} value={value} onChange={(e) => [setValue(e.target.value), setState({})]} data-testid={props.testid} />
          <button disabled={state.busy || value.trim() === props.value}>Speichern</button>
        </span>
      </label>
      <p className={state.error ? "error" : state.ok ? "ok-text" : "hint"}>{state.error ?? state.ok ?? props.hint}</p>
    </form>
  );
}

/**
 * Whether the address is confirmed, where the app sends mail (docs/14-anmeldung.md): only a
 * confirmed address gets a link for a new password. „Mail noch einmal schicken“ while one waits.
 */
function EmailState({ user, sentAt }: { user: User; sentAt: number | null }) {
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  const waiting = user.emailPending ?? (user.email && !user.emailConfirmed ? user.email : null);
  async function again() {
    setState({ busy: true });
    try {
      const { email } = await account.emailAgain();
      setState({ ok: mailSent(email) });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  const text = !user.email
    ? "Ohne E-Mail-Adresse kann dir nur der Inhaber ein neues Passwort geben."
    : user.emailPending
      ? `„${user.emailPending}“ ist noch nicht bestätigt; bis dahin gilt die bisherige Adresse. Sieh in deinem Postfach nach, auch im Spam-Ordner.`
      : user.emailConfirmed
        ? "Bestätigt"
        : NOT_CONFIRMED;
  // The last mail named, so nobody asks for another in vain (05.11.2026).
  const last = waiting && sentAt ? ` Die letzte Mail ging ${sentWhen(sentAt)} an ${waiting}; jeder Link darin gilt 7 Tage.` : "";
  return (
    <div className="email-state" data-testid="email-state">
      <p className={user.emailConfirmed && !user.emailPending ? "ok-text" : "hint"}>
        {text}
        {last}
      </p>
      {waiting && (
        <button type="button" className="link inline" disabled={state.busy} onClick={again} data-testid="email-again">
          Mail noch einmal schicken
        </button>
      )}
      {(state.ok || state.error) && <p className={state.error ? "error" : "ok-text"}>{state.error ?? state.ok}</p>}
    </div>
  );
}

function Passkeys({ passkeys, onChange }: { passkeys: Passkey[]; onChange: (p: Passkey[]) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function add() {
    setError(null);
    setBusy(true);
    try {
      onChange(await account.addPasskey(deviceName()));
    } catch (err) {
      if (!cancelled(err)) setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      {passkeys.length > 0 && (
        <ul className="list">
          {passkeys.map((p) => (
            <li key={p.id} className="entry" data-testid="passkey-row">
              <div className="entry-text">
                {p.name}
                <br />
                <span className="muted">
                  angelegt {date(p.createdAt)}
                  {p.lastUsedAt ? ` · zuletzt benutzt ${date(p.lastUsedAt)}` : ""}
                  {p.backedUp ? " · auf deinen Geräten synchronisiert" : ""}
                </span>
              </div>
              <button
                className="link danger entry-action"
                onClick={async () => {
                  if (!confirm(`Passkey „${p.name}“ entfernen? Mit ihm kannst du dich dann nicht mehr anmelden; das Passwort bleibt.`)) return;
                  await account.removePasskey(p.id).catch((err) => setError(messageOf(err)));
                  onChange(await account.passkeys());
                }}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      )}
      {passkeysSupported() ? (
        <button className="primary" disabled={busy} onClick={add} data-testid="add-passkey">
          {busy ? "Passkey hinzufügen …" : "Passkey hinzufügen"}
        </button>
      ) : (
        <p className="warn">Dieser Browser kann keine Passkeys (ab iOS 17.4 und aktuellen Browsern).</p>
      )}
      {error && <p className="error-box">{error}</p>}
    </>
  );
}

function PasswordForm() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!current) return setState({ error: "Bitte gib dein bisheriges Passwort ein." });
    if (next.length < 10) return setState({ error: "Das Passwort braucht mindestens 10 Zeichen." });
    if (next !== repeat) return setState({ error: "Die beiden Passwörter sind nicht gleich." });
    setState({ busy: true });
    try {
      await account.setPassword(current, next);
      setCurrent("");
      setNext("");
      setRepeat("");
      setState({ ok: "Passwort geändert. Deine anderen Geräte sind abgemeldet." });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <form noValidate onSubmit={submit}>
      <label>
        Bisheriges Passwort
        <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </label>
      <label>
        Neues Passwort
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        <span className="hint">Mindestens 10 Zeichen.</span>
      </label>
      <label>
        Neues Passwort wiederholen
        <input type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" />
      </label>
      <button className="primary" disabled={state.busy}>
        {state.busy ? "Passwort ändern …" : "Passwort ändern"}
      </button>
      {state.error && <p className="error-box">{state.error}</p>}
      {state.ok && <p className="ok-text">{state.ok}</p>}
    </form>
  );
}

/**
 * „Preise erfassen“ (C4), for the whole household, off by default: then booking asks for no price
 * and the pantry shows no value. Amounts of receipts are kept anyway.
 */
function PricesSetting() {
  const { store } = useStore();
  const catalog = useCatalog();
  if (!catalog) return null;
  const on = pantryPricesOn(catalog.cards.get(SETTINGS_ID));
  return (
    <section className="card" data-testid="prices">
      <h2>Preise</h2>
      <label className="check">
        <input type="checkbox" checked={on} onChange={(e) => void store.edit("card", SETTINGS_ID, { pantryPrices: e.target.checked })} data-testid="prices-on" />
        Preise erfassen
      </label>
      <p className="muted">
        {on
          ? "Beim Einlagern lässt sich ein Preis eintragen, der Vorrat zeigt seinen Wert zum Einkaufspreis."
          : "Ohne Preise: Einlagern fragt nicht danach, der Vorrat zeigt keinen Wert. Beträge aus eingelesenen Belegen bleiben trotzdem gespeichert."}
      </p>
    </section>
  );
}

/**
 * „Jahrgänge führen“ (F23), for the whole workspace. Without, a wine is one per size and a new
 * vintage goes into it; only a wine that has a vintage is kept by vintage. Switching changes no
 * booking, only the names of the wines without vintage („o. J.“) are written anew.
 */
function VintagesSetting() {
  const { store } = useStore();
  const catalog = useCatalog();
  const [busy, setBusy] = useState(false);
  if (!catalog) return null;
  const on = keepsVintages(catalog);
  async function change(next: boolean) {
    setBusy(true);
    try {
      await store.edit("card", SETTINGS_ID, { vintages: next });
      for (const a of catalog!.articles.values()) {
        if (profileOf(a.fields) !== "wein" || !isProfiled(a.fields) || typeof a.fields.vintage === "number") continue;
        const name = drinkName(a.fields, { vintages: next });
        if (name !== a.name) await store.edit("article", a.id, { name });
      }
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card" data-testid="vintages">
      <h2>Jahrgänge</h2>
      <label className="check">
        <input type="checkbox" checked={on} disabled={busy} onChange={(e) => void change(e.target.checked)} data-testid="vintages-on" />
        Jahrgänge führen
      </label>
      <p className="muted">
        {on
          ? "Jeder Jahrgang ist ein eigener Wein mit eigenem Bestand; ein neuer kommt mit „Neuer Jahrgang“. Weine ohne Jahrgang heißen „o. J.“."
          : "Ein Wein ist ein Eintrag je Größe; ein neuer Jahrgang wird auf denselben Eintrag eingelagert. Nur Weine mit eingetragenem Jahrgang werden nach Jahrgang getrennt."}
      </p>
    </section>
  );
}

/**
 * Where the product data of a scanned code comes from, once for the app instead of at each product
 * (docs/18-vorratsbuch.md, Strichcodes; F30; Martin, 05.11.2026). The ODbL asks for the source and the licence.
 */
function DataSources() {
  const link = (href: string, text: string) => (
    <a href={href} target="_blank" rel="noreferrer">
      {text}
    </a>
  );
  return (
    <section className="card" data-testid="data-sources">
      <h2>Datenquellen</h2>
      <p>
        Was VorratsBuch zu einem gescannten Strichcode vorschlägt, stammt von {link("https://world.openfoodfacts.org", "Open Food Facts")}, {link("https://world.openbeautyfacts.org", "Open Beauty Facts")} und{" "}
        {link("https://world.openproductsfacts.org", "Open Products Facts")}, Lizenz {link("https://opendatacommons.org/licenses/odbl/1-0/", "ODbL")}.
      </p>
      <p className="hint">Der Server hält einen Auszug der Produkte, die in Deutschland, Österreich, der Schweiz und Frankreich verkauft werden, und erneuert ihn jede Woche. Fehlt ein Produkt oder stimmt etwas nicht, lässt es sich dort ergänzen.</p>
    </section>
  );
}
